/**
 * PairMatcher AST Nodes
 *
 * Hierarchy:
 *   PairMatcherBlock
 *     ├─ topItems: MatcherItem[]     ← items in the TOP row
 *     ├─ bottomItems: MatcherItem[]  ← items in the BOTTOM row
 *     └─ answers: [string, string][] ← each pair is [topId, bottomId]
 *
 * Invariants enforced by the schema:
 *   1. itemIds are globally unique across top and bottom
 *   2. Every topId in an answer pair must appear in topItems
 *   3. Every bottomId in an answer pair must appear in bottomItems
 *   4. No id appears in more than one answer pair (same position)
 */

import { z } from 'zod';
import { MatcherItemSchema, MatcherItem } from './matcher-item';
import { AST_TYPES } from '../constants';

export const PairMatcherBlockSchema = z
  .object({
    type: z.literal(AST_TYPES.PAIR_MATCHER_BLOCK),
    name: z.string().min(1),

    /** Items in the top row — at least one required */
    topItems: z.array(MatcherItemSchema).min(1),

    /** Items in the bottom row — at least one required */
    bottomItems: z.array(MatcherItemSchema).min(1),

    /**
     * Each answer pair is [topId, bottomId].
     * At least one pair is required.
     */
    answers: z
      .array(z.tuple([z.string().min(1), z.string().min(1)]))
      .min(1),
  })
  .superRefine((data, ctx) => {
    // Single pass: build a registry of every declared id with its origin.
    // Catches within-section duplicates and cross-section collisions at once.
    type IdOrigin = { section: 'topItems' | 'bottomItems'; index: number };
    const idRegistry = new Map<string, IdOrigin>();

    const sections = [
      { key: 'topItems' as const, items: data.topItems },
      { key: 'bottomItems' as const, items: data.bottomItems },
    ];

    for (const { key, items } of sections) {
      for (let i = 0; i < items.length; i++) {
        const { itemId } = items[i]!;
        const existing = idRegistry.get(itemId);
        if (existing) {
          const isCrossSection = existing.section !== key;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: isCrossSection
              ? `itemId "${itemId}" appears in both TOP and BOTTOM — ids must be globally unique`
              : `Duplicate itemId "${itemId}" in ${key === 'topItems' ? 'TOP' : 'BOTTOM'} section`,
            path: [key, i, 'itemId'],
          });
        } else {
          idRegistry.set(itemId, { section: key, index: i });
        }
      }
    }

    // Derive lookup sets for answer validation from the registry.
    const topIds    = new Set(data.topItems.map(it => it.itemId));
    const bottomIds = new Set(data.bottomItems.map(it => it.itemId));

    // Single pass over answers: check references and per-position uniqueness.
    const usedTopIds    = new Set<string>();
    const usedBottomIds = new Set<string>();

    data.answers.forEach(([topId, bottomId], i) => {
      if (!topIds.has(topId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Answer pair [${topId}, ${bottomId}]: "${topId}" is not declared in TOP section`,
          path: ['answers', i, 0],
        });
      }
      if (!bottomIds.has(bottomId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Answer pair [${topId}, ${bottomId}]: "${bottomId}" is not declared in BOTTOM section`,
          path: ['answers', i, 1],
        });
      }

      if (usedTopIds.has(topId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `topId "${topId}" appears in more than one answer pair`,
          path: ['answers', i, 0],
        });
      } else {
        usedTopIds.add(topId);
      }

      if (usedBottomIds.has(bottomId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `bottomId "${bottomId}" appears in more than one answer pair`,
          path: ['answers', i, 1],
        });
      } else {
        usedBottomIds.add(bottomId);
      }
    });
  });

export type PairMatcherBlock = z.infer<typeof PairMatcherBlockSchema>;

export function createPairMatcherBlock(
  name: string,
  topItems: MatcherItem[],
  bottomItems: MatcherItem[],
  answers: [string, string][],
): PairMatcherBlock {
  return PairMatcherBlockSchema.parse({
    type: AST_TYPES.PAIR_MATCHER_BLOCK,
    name,
    topItems,
    bottomItems,
    answers,
  });
}

export function isPairMatcherBlock(value: unknown): value is PairMatcherBlock {
  return PairMatcherBlockSchema.safeParse(value).success;
}
